
import {vec3} from '../utils/matrix';
import * as math from '../utils/math';
import GeographicLib from 'geographiclib';


var MapMeasure = function(map) {
    this.map = map;
    this.config = map.config;
    this.convert = map.convert;
    this.getPhysicalSrs = this.map.getPhysicalSrs();
    this.navigationSrs = this.map.getNavigationSrs();
    this.publicSrs = this.map.getPublicSrs();
    this.navigationSrsInfo = this.navigationSrs.getSrsInfo();
    this.isProjected = this.navigationSrs.isProjected();

    var res = this.getSpatialDivisionNodeDepths();

    this.minDivisionNodeDepth = res[0];
    this.maxDivisionNodeDepth = res[1];
};


MapMeasure.prototype.getSpatialDivisionNodeAndExtents = function(id) {
    var nodes = this.map.referenceFrame.getSpatialDivisionNodes();

    var bestNode = null;
    //var bestLod = -1;
    var bestNodeCoords = [0,0], shift;
    //var bestExtents = {ll:[0,0], ur:[1,1]};

    for (var i = 0, li = nodes.length; i < li; i++) {
        var node = nodes[i];
        
        //has division node this tile node 
        //var shift = node.id[0] - this.lodRange[0];
        shift = id[0] - node.id[0];

        if (shift >= 0) {
            var x = id[1] >> shift;
            var y = id[2] >> shift;
            
            if (node.id[1] == x && node.id[2] == y) {
                bestNode = node;
                //bestLod = node.id[0];
                //bestExtents = node.extents;
                bestNodeCoords = [node.id[1] << shift, node.id[2] << shift];                
            }
        }
    }
    
    if (!bestNode) {
        return null;
    }
    
    shift = id[0] - bestNode.id[0];
    
    var factor = 1.0 / Math.pow(2, shift);
    var ur = bestNode.extents.ur;
    var ll = bestNode.extents.ll;
    
    // extents use ll/ur corners, but tile ids use a top-left grid (y grows downward)
    
    var dx = (ur[0] - ll[0]) * factor; 
    var dy = (ll[1] - ur[1]) * factor;
    
    var nx = id[1] - bestNodeCoords[0];
    var ny = id[2] - bestNodeCoords[1];

    return [bestNode, [[ll[0] + dx * nx, ur[1] + dy * ny], [ll[0] + dx * (nx+1), ur[1] + dy * (ny+1)] ]];
};


MapMeasure.prototype.getSpatialDivisionNodeFromId = function(id) {
    var shift = id[0] - this.maxDivisionNodeDepth;
    var nx = id[1] >> shift;
    var ny = id[2] >> shift;
    
    return this.map.referenceFrame.nodesMap['' + this.maxDivisionNodeDepth + '.'  + nx + '.' + ny];
};


MapMeasure.prototype.getSpatialDivisionNodeAndExtents2 = function(id, res, divisionNode) {
    if (!divisionNode) {
        return [null, 0,0,0,0];
    }
    
    var shift = id[0] - divisionNode.id[0];
    var factor = 1.0 / Math.pow(2, shift);
    var ur = divisionNode.extents.ur;
    var ll = divisionNode.extents.ll;
    
    // extents use ll/ur corners, but tile ids use a top-left grid (y grows downward)
    
    var dx = (ur[0] - ll[0]) * factor; 
    var dy = (ll[1] - ur[1]) * factor;
    
    var nx = id[1] - (divisionNode.id[1] << shift);
    var ny = id[2] - (divisionNode.id[2] << shift);
    
    res[0] = divisionNode;
    res[1] = ll[0] + dx * nx;
    res[2] = ur[1] + dy * ny;
    res[3] = ll[0] + dx * (nx+1);
    res[4] = ur[1] + dy * (ny+1);
};


/**
 * @param {[number, number, number]} id tile id as [lod, x, y]
 * @param {number=} height physical-corner sampling height
 * @returns {import('../types').NodeInformation | null}
 */
MapMeasure.prototype.getNodeInformation = function(id, height) {
    if (!id || id.length < 3) {
        return null;
    }

    height = (height == null) ? 0 : height;
    const res = this.getSpatialDivisionNodeAndExtents(id);

    if (!res || !res[0]) {
        return null;
    }

    const divisionNode = res[0];
    const ll = [res[1][0][0], res[1][1][1]];
    const ur = [res[1][1][0], res[1][0][1]];

    const physicalCorners = {
        ul : divisionNode.getPhysicalCoords([ll[0], ur[1], height], true),
        ur : divisionNode.getPhysicalCoords([ur[0], ur[1], height], true),
        lr : divisionNode.getPhysicalCoords([ur[0], ll[1], height], true),
        ll : divisionNode.getPhysicalCoords([ll[0], ll[1], height], true)
    };

    const upLeft = vec3.subtract(physicalCorners.ul, physicalCorners.ll, []);
    const upRight = vec3.subtract(physicalCorners.ur, physicalCorners.lr, []);
    const upVector = vec3.scale(vec3.add(upLeft, upRight, []), 0.5);
    vec3.normalize(upVector);

    return {
        id : [id[0], id[1], id[2]],
        height : height,
        srs : divisionNode.srs,
        extents : {
            ll : ll,
            ur : ur
        },
        divisionNode : divisionNode,
        physicalCorners : physicalCorners,
        upVector : upVector
    };
};


MapMeasure.prototype.getSpatialDivisionNodeDepths = function() {
    var nodes = this.map.referenceFrame.getSpatialDivisionNodes();
    var maxLod = -1;
    var minLod = Number.MAX_VALUE;

    for (var i = 0, li = nodes.length; i < li; i++) {
        var node = nodes[i];

        if (node.id[0] < minLod) {
            minLod = node.id[0];
        } 
        
        if (node.id[0] > maxLod) {
            maxLod = node.id[0];
        } 
    }

    return [minLod, maxLod];
};


MapMeasure.prototype.getDistance = function(coords, coords2, includingHeight, usePublic) {
    var sourceSrs = usePublic ? this.publicSrs : this.navigationSrs;
    var p1 = this.getPhysicalSrs.convertCoordsFrom(coords,  sourceSrs);
    var p2 = this.getPhysicalSrs.convertCoordsFrom(coords2, sourceSrs);
    var d = 0;

    var dx = p2[0] - p1[0];
    var dy = p2[1] - p1[1];
    var dz = p2[2] - p1[2];

    var dd = Math.sqrt(dx*dx + dy*dy + dz*dz);
    var navigationSrsInfo = this.navigationSrsInfo;

    if (!this.isProjected) {
        var geod = this.getGeodesic(); //new GeographicLib["Geodesic"]["Geodesic"](navigationSrsInfo["a"],
                                       //                   (navigationSrsInfo["a"] / navigationSrsInfo["b"]) - 1.0);

        var r = geod.Inverse(coords[1], coords[0], coords2[1], coords2[0]);

        if (r.s12 > (navigationSrsInfo['a'] * 2 * Math.PI) / 4007.5) { //aprox 10km for earth
            if (includingHeight) {
                return [Math.sqrt(r.s12*r.s12 + dz*dz), -r.azi1, dd];
            } else {
                return [r.s12, -r.azi1, dd];
            }
        } else {
            if (includingHeight) {
                return [Math.sqrt(dx*dx + dy*dy + dz*dz), -r.azi1, dd];
            } else {
                return [r.s12, -r.azi1, dd];
            }
        }

    } else {
        return [Math.sqrt(dx*dx + dy*dy), math.degrees(Math.atan2(dx, dy)), dd];
    }
};


MapMeasure.prototype.getGeodesic = function() {
    var navigationSrsInfo = this.navigationSrsInfo;

    var geodesic = new GeographicLib.Geodesic.Geodesic(navigationSrsInfo['a'],
                                                      (navigationSrsInfo['a'] / navigationSrsInfo['b']) - 1.0);

    return geodesic;
};


MapMeasure.prototype.getAzimuthCorrection = function(coords, coords2) {
    if (!this.getNavigationSrs().isProjected()) {
        var geodesic = this.getGeodesic();
        var r = geodesic.Inverse(coords[0], coords[1], coords2[0], coords2[1]);
        var ret = (r.azi1 - r.azi2);
        if (isNaN(ret)) {
            ret = 0;
        } 
        return ret; 
    }
    return 0;
};


MapMeasure.prototype.getNewNED = function(coords, returnMatrix) {
    var centerCoords = this.convert.convertCoords([coords[0], coords[1], 0], 'navigation', 'physical');
    var upCoords, rightCoords;

    if (this.isProjected) {
        upCoords = this.convert.convertCoords([coords[0], coords[1] + 100, 0], 'navigation', 'physical');
        rightCoords = this.convert.convertCoords([coords[0] + 100, coords[1], 0], 'navigation', 'physical');
    } else {
        //get NED for latlon coordinates
        //http://www.mathworks.com/help/aeroblks/directioncosinematrixeceftoned.html
        /*        
        var coords = this.position.getCoords();
        var lon = math.radians(coords[0]);
        var lat = math.radians(coords[1]);

        //NED vectors for sphere
        var east = [-Math.sin(lat)*Math.cos(lon), -Math.sin(lat)*Math.sin(lon), Math.cos(lat)];
        var direction = [-Math.sin(lon), Math.cos(lon), 0];
        var north = [-Math.cos(lat)*Math.cos(lon), -Math.cos(lat)*Math.sin(lon), -Math.sin(lat)];

        north = vec3.negate(north);
        east  = vec3.negate(east);
        
        //get elipsoid factor
        var navigationSrsInfo = this.getNavigationSrs().getSrsInfo();
        var factor = navigationSrsInfo["b"] / navigationSrsInfo["a"];

        //flaten vectors
        north[2] *= factor;
        east[2] *= factor;
        direction[2] *= factor;

        //normalize vectors
        north = vec3.normalize(north);
        east  = vec3.normalize(east);
        direction = vec3.normalize(direction);
        */

        var cy = (coords[1] + 90) + 0.0001;
        var cx = (coords[0] + 180) + 0.0001;

        if (cy < 0 || cx > 180) { //if we are out of bounds things start to be complicated
            var geodesic = this.getGeodesic();
        
            //up coords
            var r = geodesic.Direct(coords[1], coords[0], 0, -100);
            upCoords = this.convert.convertCoords([r.lon2, r.lat2, 0], 'navigation', 'physical');
    
            //right coords
            r = geodesic.Direct(coords[1], coords[0], 90, -100);
            rightCoords = this.convert.convertCoords([r.lon2, r.lat2, 0], 'navigation', 'physical');
        } else {
            // substraction instead of addition is probably case of complicated view matrix calculation
            upCoords = this.convert.convertCoords([coords[0], coords[1] + 0.0001, 0], 'navigation', 'physical');
            rightCoords = this.convert.convertCoords([coords[0] + 0.0001, coords[1], 0], 'navigation', 'physical');
        }
    }

    var up = [upCoords[0] - centerCoords[0],
        upCoords[1] - centerCoords[1],
        upCoords[2] - centerCoords[2]]; 

    var right = [rightCoords[0] - centerCoords[0],
        rightCoords[1] - centerCoords[1],
        rightCoords[2] - centerCoords[2]]; 

    var dir = [0,0,0];
    vec3.normalize(up);
    vec3.normalize(right);
    vec3.cross(up, right, dir);
    vec3.normalize(dir);

    if (returnMatrix) {
        var east = right;
        var direction = up;
        var north = dir;

        return [
            east[0], east[1], east[2], 0,
            north[0], north[1], north[2], 0,
            direction[0], direction[1], direction[2], 0,
            0, 0, 0, 1
        ];        
    }

    return {
        east  : right, 
        direction : up,
        north : dir        
    };
};

export default MapMeasure;
